import loadable from '@loadable/component'
import React from 'react'
import { Route, Routes } from 'react-router-dom'
import DashboardLoader from '../components/common/DashboardLoader'
import { ADMIN_LINK, REFERRAL_LINK } from '../variables'
import * as Sentry from '@sentry/react'

const AsyncMainDashboard = loadable(() => import('./DashboardMain'), {
  fallback: <DashboardLoader />
})

const AuthRouter = loadable(() => import('./AuthRouter'), {
  fallback: <DashboardLoader />
})

const AdminRouter = loadable(() => import('./AdminRouter'), {
  fallback: <DashboardLoader />
})

const ReferralRouter = loadable(() => import('./ReferralRouter'), {
  fallback: <DashboardLoader />
})

const AsyncAppointmentQrCode = loadable(() => import('../components/home/qrCode/AppointmentQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncRegisterQrCode = loadable(() => import('../components/home/qrCode/RegisterQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncLeadQrCode = loadable(() => import('../components/home/qrCode/LeadQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncMedicalQrCode = loadable(() => import('../components/home/qrCode/MedicalQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncPatientTimelineQrCode = loadable(() => import('../components/home/qrCode/PatientTimelineQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncInvoiceQrCode = loadable(() => import('../components/home/qrCode/InvoiceQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncPaymentQrCode = loadable(() => import('../components/home/qrCode/PaymentQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncQuotationQrCode = loadable(() => import('../components/home/qrCode/QuotationQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncChartQrCode = loadable(() => import('../components/home/qrCode/ChartQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncExpenseQrCode = loadable(() => import('../components/home/qrCode/ExpenseQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncLabOrderQrCode = loadable(() => import('../components/home/qrCode/LabOrderQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncPrescriptionQrCode = loadable(() => import('../components/home/qrCode/PrescriptionQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncEncounterQrCode = loadable(() => import('../components/home/qrCode/EncounterQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncFormQrCode = loadable(() => import('../components/home/qrCode/FormQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncFreeFormQrCode = loadable(() => import('../components/home/qrCode/FreeFormQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncClinicHomePage = loadable(() => import('../components/home/ClinicHomePage/ClinicHomePage'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncPrint = loadable(() => import('../components/home/qrCode/PrintPage'), {
  fallback: <DashboardLoader className="auth" />
})

const AsyncCustomFormQrCode = loadable(() => import('../components/home/qrCode/CustomFormQrCode'), {
  fallback: <DashboardLoader className="auth" />
})

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const MainRouter = () => {
  return (
    <SentryRoutes>
      <Route path="/auth/*" element={<AuthRouter />} />
      <Route path={`/${ADMIN_LINK}/*`} element={<AdminRouter />} />
      <Route path={`/${REFERRAL_LINK}/*`} element={<ReferralRouter />} />
      <Route path="/appointment/:appointment" element={<AsyncAppointmentQrCode />} />
      <Route path="/register/:register" element={<AsyncRegisterQrCode />} />
      <Route path="/lead/:leadLink" element={<AsyncLeadQrCode />} />
      <Route path="/lead/:leadLink/:language" element={<AsyncLeadQrCode />} />
      <Route path="/medical/:patient" element={<AsyncMedicalQrCode />} />
      <Route path="/chart/:patient" element={<AsyncChartQrCode />} />
      <Route path="/expense/:expense" element={<AsyncExpenseQrCode />} />
      <Route path="/invoice/:invoice" element={<AsyncInvoiceQrCode />} />
      <Route path="/payment/:payment" element={<AsyncPaymentQrCode />} />
      <Route path="/quotation/:quotation" element={<AsyncQuotationQrCode />} />
      <Route path="/prescription/:prescription" element={<AsyncPrescriptionQrCode />} />
      <Route path="/encounter/:patientEncounter" element={<AsyncEncounterQrCode />} />
      <Route path="/form/:formInstance" element={<AsyncFormQrCode />} />
      <Route path="/f/:form" element={<AsyncFreeFormQrCode />} />
      <Route path="/lab-order/:labOrder" element={<AsyncLabOrderQrCode />} />
      <Route path="/print/:type/:id/:branch/:token" element={<AsyncPrint />} />
      <Route path="/appointment/:appointment/:language" element={<AsyncAppointmentQrCode />} />
      <Route path="/register/:register/:language" element={<AsyncRegisterQrCode />} />
      <Route path="/medical/:patient/:language" element={<AsyncMedicalQrCode />} />
      <Route path="/chart/:patient/:language" element={<AsyncChartQrCode />} />
      <Route path="/expense/:expense/:language" element={<AsyncExpenseQrCode />} />
      <Route path="/invoice/:invoice/:language" element={<AsyncInvoiceQrCode />} />
      <Route path="/payment/:payment/:language" element={<AsyncPaymentQrCode />} />
      <Route path="/quotation/:quotation/:language" element={<AsyncQuotationQrCode />} />
      <Route path="/prescription/:prescription/:language" element={<AsyncPrescriptionQrCode />} />
      <Route path="/encounter/:patientEncounter/:language" element={<AsyncEncounterQrCode />} />
      <Route path="/form/:formInstance/:language" element={<AsyncFormQrCode />} />
      <Route path="/f/:form/:language" element={<AsyncFreeFormQrCode />} />
      <Route path="/form/:formInstance/:language/:token" element={<AsyncFormQrCode />} />
      <Route path="/lab-order/:labOrder/:language" element={<AsyncLabOrderQrCode />} />
      <Route path="/timeline/:timeline/:language" element={<AsyncPatientTimelineQrCode />} />
      <Route path="/timeline/:timeline" element={<AsyncPatientTimelineQrCode />} />
      <Route path="/print/:type/:id/:branch/:token/:language" element={<AsyncPrint />} />
      <Route path="/c/:loginName" element={<AsyncClinicHomePage />} />
      <Route path="/c/:loginName/:language" element={<AsyncClinicHomePage />} />
      <Route path="*" element={<AsyncMainDashboard />} />
      <Route path="/custom-form/s/:submissionId/:language" element={<AsyncCustomFormQrCode />} />
    </SentryRoutes>
  )
}

export default MainRouter
